import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Globe, BookOpen, Clock, Users, Star, Tag, ExternalLink } from '../../components/CustomIcons';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_COLORS = {
  Technology: '#3B82F6', Business: '#8B5CF6', Healthcare: '#10B981',
  Logistics: '#F59E0B', Academic: '#F97316', 'Skilled Trades': '#EF4444',
};

export default function ProviderPublicProfile() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    fetch(`${API_URL}/api/marketplace/providers/${id}`)
      .then(r => r.json())
      .then(d => setData(d))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-page)', padding: '32px 24px' }}>
      <div style={{ maxWidth: '900px', margin: '0 auto' }}>
        <div className="skeleton" style={{ height: '200px', borderRadius: '16px', marginBottom: '24px' }} />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '14px' }}>
          {[...Array(3)].map((_, i) => <div key={i} className="skeleton" style={{ height: '240px', borderRadius: '14px' }} />)}
        </div>
      </div>
    </div>
  );

  if (error || !data) return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-page)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ textAlign: 'center' }}>
        <p style={{ color: 'var(--text-muted)', fontSize: '16px', marginBottom: '16px' }}>Provider not found.</p>
        <Link to="/marketplace/catalog" className="btn-secondary" style={{ textDecoration: 'none' }}>Back to Catalog</Link>
      </div>
    </div>
  );

  const { provider, courses } = data;

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-page)', padding: '32px 24px' }} data-testid="provider-public-profile">
      <div style={{ maxWidth: '900px', margin: '0 auto' }}>

        {/* Back */}
        <Link to="/marketplace/catalog" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', color: 'var(--text-muted)', textDecoration: 'none', fontSize: '13px', marginBottom: '20px', fontFamily: 'Inter, sans-serif' }}>
          <ArrowLeft size={14} /> Back to Catalog
        </Link>

        {/* Provider Header */}
        <div className="card" style={{ padding: '28px', marginBottom: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '20px', flexWrap: 'wrap' }}>
            <div style={{ width: '64px', height: '64px', borderRadius: '14px', background: 'var(--orange-muted)', border: '2px solid rgba(249,115,22,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <BookOpen size={28} style={{ color: 'var(--orange)' }} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginBottom: '6px' }}>
                <h1 style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 800, fontSize: '24px', color: 'var(--text-primary)', margin: 0 }}>{provider.name}</h1>
                <span style={{ fontSize: '11px', fontWeight: 700, padding: '3px 10px', borderRadius: '5px', background: 'var(--success-bg)', color: 'var(--success)' }}>Verified Partner</span>
              </div>
              <p style={{ fontSize: '13px', color: 'var(--text-muted)', margin: '0 0 8px 0' }}>{provider.category}</p>
              <p style={{ fontSize: '14px', color: 'var(--text-secondary)', margin: '0 0 14px 0', lineHeight: 1.6 }}>{provider.description}</p>

              <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <BookOpen size={14} style={{ color: 'var(--orange)' }} />
                  <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>{courses.length} courses</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Users size={14} style={{ color: 'var(--orange)' }} />
                  <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>{provider.learners_count || courses.reduce((a, c) => a + (c.enrolled_count || 0), 0)} learners</span>
                </div>
                {provider.rating > 0 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <Star size={14} style={{ color: 'var(--orange)' }} fill="var(--orange)" />
                    <span style={{ fontSize: '13px', color: 'var(--orange)', fontWeight: 600 }}>{provider.rating}</span>
                  </div>
                )}
                {provider.website && (
                  <a href={provider.website} target="_blank" rel="noopener noreferrer"
                    style={{ display: 'flex', alignItems: 'center', gap: '6px', color: 'var(--orange)', textDecoration: 'none', fontSize: '13px', fontWeight: 600 }}>
                    <Globe size={14} /> Website <ExternalLink size={11} />
                  </a>
                )}
              </div>
            </div>
          </div>

          {/* Tags */}
          {(provider.tags || []).length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '16px', paddingTop: '16px', borderTop: '1px solid var(--border-subtle)' }}>
              {provider.tags.map(tag => (
                <span key={tag} className="badge-grey">
                  <Tag size={10} /> {tag}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Courses */}
        <h2 style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: '19px', color: 'var(--text-primary)', margin: '0 0 16px 0' }}>
          Available Courses ({courses.length})
        </h2>

        {courses.length === 0 ? (
          <div className="card" style={{ padding: '40px', textAlign: 'center', color: 'var(--text-muted)' }}>
            No courses published yet.
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '16px' }}>
            {courses.map(course => {
              const color = CATEGORY_COLORS[course.category] || 'var(--orange)';
              return (
                <div key={course.id} className="card" style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '10px' }}
                  onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 10px 28px rgba(0,0,0,0.3)'; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = ''; }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: '11px', fontWeight: 700, padding: '2px 8px', borderRadius: '4px', background: `${color}18`, color, border: `1px solid ${color}33` }}>{course.category}</span>
                    {course.price_credits === 0
                      ? <span style={{ fontSize: '12px', color: 'var(--success)', fontWeight: 700 }}>Free</span>
                      : <span style={{ fontSize: '12px', color: 'var(--orange)', fontWeight: 700 }}>{course.price_credits.toLocaleString()} credits</span>
                    }
                  </div>
                  <h3 style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: '15px', color: 'var(--text-primary)', margin: 0, lineHeight: 1.35 }}>{course.title}</h3>
                  <p style={{ fontSize: '13px', color: 'var(--text-secondary)', margin: 0, lineHeight: 1.5, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{course.description}</p>
                  <div style={{ display: 'flex', gap: '12px', marginTop: 'auto', paddingTop: '10px', borderTop: '1px solid var(--border-subtle)' }}>
                    <span style={{ fontSize: '12px', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '4px' }}><Clock size={12} /> {course.duration_hours}h</span>
                    <span style={{ fontSize: '12px', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '4px' }}><Users size={12} /> {course.enrolled_count}</span>
                    {course.rating > 0 && <span style={{ fontSize: '12px', color: 'var(--orange)', display: 'flex', alignItems: 'center', gap: '4px' }}><Star size={12} fill="var(--orange)" /> {course.rating}</span>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
